import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const form7205 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 7205: Section 179D Commercial Building Deduction Guide"
          description="Learn how to complete Form 7205 to claim the Section 179D deduction, determine eligibility, report energy-efficient property, and calculate your deduction."
          keywords=""
          canonical="resources/business-tax/form-7205/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight">7205</span>
          </span>{' '}
          <span>Instructions</span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn how to complete Form 7205, determine eligibility, and calculate
          the Section 179D <br />
          energy-efficient commercial buildings deduction.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7205 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Understand the purpose of IRS Form 7205
                      </strong>{' '}
                      and how it is used to calculate and claim the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Section 179D Energy Efficient Commercial Buildings
                        Deduction
                      </strong>{' '}
                      for qualifying energy-efficient property.
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Learn who can claim the Section 179D deduction
                      </strong>{' '}
                      and how the rules apply to building owners and eligible
                      designers of qualifying property installed in certain
                      tax-exempt buildings.
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Explore how to complete Form 7205 line by line,
                      </strong>{' '}
                      including building information, energy savings or EUI
                      reduction, deduction limits, qualifying property costs,
                      certifications, and designer allocation details.
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Understand which property may qualify for the deduction,
                      </strong>{' '}
                      including{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Energy Efficient Commercial Building Property (EECBP)
                      </strong>{' '}
                      and{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Energy Efficient Building Retrofit Property (EEBRP),
                      </strong>{' '}
                      and the applicable energy-efficiency requirements.
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7205, Energy Efficient Commercial Buildings Deduction, is
                  an IRS form used to calculate and claim the
                  <span style={{ fontWeight: 600 }}>
                    <Link
                      href="https://www.irs.gov/credits-deductions/energy-efficient-commercial-buildings-deduction"
                      target="_blank"
                      sx={{ color: '#2020ff' }}
                    >
                      Section 179D
                    </Link>
                    &nbsp;deduction for qualifying energy-efficient
                    property{' '}
                  </span>
                  installed in commercial buildings. Building owners and
                  eligible designers must attach Form 7205 to their federal
                  income tax return to report
                  <span style={{ fontWeight: 600 }}>
                    energy-efficient commercial building property (EECBP) or
                    energy-efficient building retrofit property (EEBRP),{' '}
                  </span>
                  document required certifications, and determine the allowable
                  deduction based on energy savings, building square footage,
                  and applicable deduction limits.
                </Typography>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is IRS Form 7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#whatsnewform7205"
                          sx={{ color: '#0202ff' }}
                        >
                          What's New in Form 7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#purposeform7205" sx={{ color: '#0202ff' }}>
                          What is the Purpose of Form 7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#whomustfileform7205"
                          sx={{ color: '#0202ff' }}
                        >
                          Who Must File Form 7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whattax7205" sx={{ color: '#0202ff' }}>
                          What Tax Return Is Form 7205 Attached To?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#Whoqualifies" sx={{ color: '#0202ff' }}>
                          Who Qualifies for the Section 179D Deduction?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#whattypesofdeduction"
                          sx={{ color: '#0202ff' }}
                        >
                          What Types of Property Qualify for the Deduction?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#linebyline" sx={{ color: '#0202ff' }}>
                          Line-by-Line Instructions for Form 7205
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part1" sx={{ color: '#0202ff' }}>
                                Part I: Building and Energy Efficient Property
                                (EEP) Information
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part2" sx={{ color: '#0202ff' }}>
                                Part II: Computation of Energy Efficient
                                Commercial Buildings Deduction Amount
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part3" sx={{ color: '#0202ff' }}>
                                Part III: Certification Information for Each
                                Property Listed in Part I
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem
                            sx={{ py: 0.5, pb: 0, alignItems: 'flex-start' }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part4" sx={{ color: '#0202ff' }}>
                                Part IV: Designer Allocation Information for
                                Each Property Listed in Part I (to be completed
                                by Designer only)
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common7205" sx={{ color: '#0202ff' }}>
                          What Are the Common Filing Mistakes to Avoid on Form
                          7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatshould7205" sx={{ color: '#0202ff' }}>
                          What Should You Know Before Filing Form 7205?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#faq" sx={{ color: '#0202ff' }}>
                          Frequently Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is IRS Form 7205?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/i7205.pdf"
                    target="_blank"
                    sx={{ color: '#2020ff' }}
                  >
                    IRS Form 7205,
                  </Link>
                  &nbsp;Energy Efficient Commercial Buildings Deduction is used
                  to calculate and claim the tax deduction under Section 179D
                  for qualifying energy-efficient commercial building property.
                  The form is generally used by building owners and, in certain
                  cases, designers of energy-efficient property installed in
                  buildings owned by specified tax-exempt entities. It covers
                  qualifying improvements to interior lighting, heating,
                  cooling, ventilation, hot water systems, and the building
                  envelope.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="whatsnewform7205"
                >
                  What's New in Form 7205
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Section 179D termination date:
                    </strong>
                    Under P.L. 119-21, commonly known as the One Big Beautiful
                    Bill Act, the Section 179D energy efficient commercial
                    buildings deduction is terminated for qualifying property
                    <strong style={{ fontWeight: 600 }}>
                      the construction of which begins after June 30, 2026
                    </strong>
                    .
                  </li>
                  <li>
                    Therefore, the deduction remains available for qualifying
                    property whose construction begins on or before June 30,
                    2026, subject to the applicable Section 179D
                    requirements.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="purposeform7205"
                >
                  What is the Purpose of Form 7205
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The primary purpose of Form 7205 is to calculate and claim the
                  Energy Efficient Commercial Buildings Deduction under Section
                  179D.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS requires this form to:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Calculate the Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers use the form to determine the amount of deduction
                  available based on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Building square footage </li>
                  <li>Energy savings achieved </li>
                  <li>Cost of qualifying property </li>
                  <li>Applicable deduction limitations</li>
                  <li>Prior deductions claimed for the building </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Report Energy-Efficient Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The form identifies the specific building where the property
                  was installed and confirms that the property meets Section
                  179D requirements.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Verify Eligibility
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7205 helps establish that the taxpayer is eligible to
                  claim the deduction, either as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>The building owner, or </li>
                  <li>
                    An eligible designer who received an allocation from a
                    qualifying tax-exempt building owner{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Document Required Certifications
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS requires certification from qualified professionals to
                  support the deduction. Form 7205 records information about
                  these certifications.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Claim the Final Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After all calculations are completed, Form 7205 determines the
                  allowable Section 179D deduction that can be reported on the
                  taxpayer's federal income tax return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whomustfileform7205"
                >
                  Who must file form 7205?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Individuals and business entities claiming a Section 179D
                  deduction for qualifying energy-efficient property (EEP)
                  placed in service during the tax year must file Form 7205.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Eligible filers include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Commercial building owners claiming the deduction for
                    qualifying property installed in their buildings.{' '}
                  </li>
                  <li>
                    Designers of energy-efficient property installed in
                    buildings owned by eligible tax-exempt entities when the
                    deduction has been properly allocated to them.{' '}
                  </li>
                  <li>
                    Partnerships, corporations, S corporations, trusts, estates,
                    and individuals claiming the deduction.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="whattax7205"
                >
                  What Tax return is Form 7205 Attached To?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Depending on the type of taxpayer, Form 7205 may be attached
                  to one of the following returns
                </Typography>
                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Individual">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Tax Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Federal tax Return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Individual </TableCell>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/f1040.pdf"
                              target="_blank"
                              sx={{ color: '#2020ff' }}
                            >
                              From 1040
                            </Link>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Partnership </TableCell>
                          <TableCell>
                            <Link
                              href="/resources/business-tax/form-1065-instructions/"
                              sx={{ color: '#2020ff' }}
                            >
                              Form 1065
                            </Link>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>S corporation </TableCell>
                          <TableCell>
                            <Link
                              href="/resources/business-tax/form-1120-s-instructions/"
                              sx={{ color: '#2020ff' }}
                            >
                              From 1120-S
                            </Link>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>C Corporation </TableCell>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/f1120.pdf"
                              target="_blank"
                              sx={{ color: '#2020ff' }}
                            >
                              From 1120
                            </Link>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Trust or Estate </TableCell>
                          <TableCell>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/f1041.pdf"
                              target="_blank"
                              sx={{ color: '#2020ff' }}
                            >
                              Form 1041
                            </Link>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Include Form 7205 with your federal income tax
                  return when claiming a deduction for qualifying
                  energy-efficient commercial building property.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="Whoqualifies"
                >
                  Who Qualifies for the Section 179D Deduction?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  The following taxpayers may qualify for the deduction:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Owners of commercial buildings located in the United
                    States.{' '}
                  </li>
                  <li>
                    Designers of qualifying energy-efficient property installed
                    in buildings owned by:{' '}
                    <Box
                      component="ul"
                      sx={{ mb: 2, mt: 2, '& li': { mb: 1.2 } }}
                    >
                      <li>Tax-exempt organisations </li>
                      <li>Government entities </li>
                      <li>Indian tribal governments </li>
                      <li>Alaska Native corporations </li>
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Projects meeting prevailing wage and apprenticeship
                  requirements may qualify for increased deduction amounts.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="whattypesofdeduction"
                >
                  What Type of Property Qualifies for the Deduction?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Qualifying Energy Efficient Property (EEP) includes:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Energy Efficient Commercial Building Property (EECBP)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Property that is depreciable. </li>
                  <li>
                    Property that is installed in a U.S. commercial
                    building.{' '}
                  </li>
                  <li>
                    Property that is part of:{' '}
                    <Box
                      component="ul"
                      sx={{ mb: 2, mt: 2, '& li': { mb: 1.2 } }}
                    >
                      <li>Interior lighting systems </li>
                      <li>Heating systems </li>
                      <li>Cooling systems </li>
                      <li>entilation systems </li>
                      <li>Hot water systems </li>
                      <li>Building envelope </li>
                    </Box>
                  </li>
                  <li>
                    Reduces annual energy and power costs by at least 25%
                    compared to a reference building meeting ASHRAE Standard
                    90.1 requirements.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Energy Efficient Building Retrofit Property (EEBRP)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Property that:
                </Typography>
                <Box component="ul" sx={{ mb: 2, mt: 2, '& li': { mb: 1.2 } }}>
                  <li>Is depreciable. </li>
                  <li>Is installed pursuant to a qualified retrofit plan. </li>
                  <li>Is installed in a qualified building. </li>
                  <li>Is certified by a qualified professional. </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="linebyline"
                >
                  Line-by-Line Instructions for Form 7205
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Basic Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before completing
                  <strong style={{ fontWeight: 600 }}> Form 7205, </strong>
                  enter the taxpayer information exactly as it appears on the
                  federal income tax return and identify who is claiming the
                  Section 179D deduction.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/line-by-line-7205-img.webp" />
                  <source srcSet="/assets/images/line-by-line-7205-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/line-by-line-7205-img.png"
                    loading="lazy"
                    alt="Form 7205 basic information showing name and EIN"
                    width={760}
                    height={124}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Name(s) Shown on Return
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxpayer's name exactly as it appears on the federal
                  income tax return to which Form 7205 is attached.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Identifying Number
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxpayer's identifying number, such as an
                  <strong style={{ fontWeight: 600 }}>SSN, EIN, or TIN</strong>,
                  as shown on the tax return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Claiming Deduction As (Check One)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Building Owner:</strong>
                  Check this box if you own the building where the qualifying
                  energy-efficient property was installed and are claiming the
                  <strong style={{ fontWeight: 600 }}>Section 179D</strong>
                  deduction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    Designer of Energy Efficient property:
                  </strong>
                  Check this box if you are claiming a deduction allocated to
                  you by a qualifying tax-exempt building owner.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Select only one box based on your eligibility.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="part1"
                >
                  Part I: Building and Energy Efficient Property (EEP)
                  Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/building-and-energy-efficient-7205-img.webp" />
                  <source srcSet="/assets/images/building-and-energy-efficient-7205-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/building-and-energy-efficient-7205-img.png"
                    loading="lazy"
                    alt="Form 7205 Part I showing building and energy efficient property (EEP) information"
                    width={760}
                    height={261}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (a): Building Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the building's name and complete address, including the
                  street address, city, state, and ZIP code where the
                  energy-efficient property was installed. If reporting multiple
                  buildings, enter each building on a separate line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (b): Date Placed in Service
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the energy-efficient property was placed in
                  service and became available for use.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (c): Energy Savings Percentage or EUI Reduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable energy savings percentage calculated
                  using the Performance Rating Method for EECBP, or the
                  applicable Energy Use Intensity (EUI) reduction for EEBRP.
                  Round the percentage to two decimal places.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (d): Increased Deduction Eligibility
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the project qualifies for the increased
                  Section 179D deduction amount, such as meeting prevailing wage
                  and apprenticeship requirements or if installation began
                  before January 29, 2023.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (e): Energy Efficient Building Retrofit
                  Property (EEBRP)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the property was installed under a qualified
                  energy-efficient building retrofit plan. If the same building
                  includes both EECBP and EEBRP property, report each on a
                  separate line.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (f): Potential Deduction Amount Per Square Foot
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate and enter the potential deduction amount per square
                  foot using the Form 7205 worksheet. The allowable amount may
                  be limited based on deductions previously claimed for the
                  building.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1, Column (g): Building Square Footage
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the total square footage of the building used to
                  calculate the deduction.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="part2"
                >
                  Part II: Computation of Energy Efficient Commercial Buildings
                  Deduction Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part-ii-computation-of-energy-efficient-7025-img.webp" />
                  <source srcSet="/assets/images/part-ii-computation-of-energy-efficient-7025-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1065-schb1-img.png"
                    loading="lazy"
                    alt="Form 7205 Part II showing computation of the energy efficient commercial buildings deduction amount"
                    width={760}
                    height={348}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (a): Prior Year Deduction Amount
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the total
                    <strong style={{ fontWeight: 600 }}>
                      per-square-foot amount of Section 179D deductions
                    </strong>
                    claimed for the building during the prior 3 tax years.
                  </li>
                  <li>
                    Use a 4-tax-year period if the deduction is allowable to the
                    designer. Include amounts claimed by you or other parties,
                    including previous building owners.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (b): Remaining Deduction Limit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the resulting difference in Column (b). Subtract the
                  amount shown in Line 2, Column (a) from the maximum allowable
                  deduction for the tax year (as specified in the chart below).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  The maximum allowable amount for each tax year is provided in
                  the chart below.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1.5 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 600,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Tax year
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Maximum full amount allowed
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Maximum partial amount allowed
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            All years beginning before January 1, 2021
                          </TableCell>
                          <TableCell>$1.80</TableCell>
                          <TableCell>$0.60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2021
                          </TableCell>
                          <TableCell>$1.82</TableCell>
                          <TableCell>$0.61</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2022
                          </TableCell>
                          <TableCell>$1.88</TableCell>
                          <TableCell>$0.63</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2023
                          </TableCell>
                          <TableCell>$5.36</TableCell>
                          <TableCell>$1.07</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2024
                          </TableCell>
                          <TableCell>$5.65</TableCell>
                          <TableCell>$1.13</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2025
                          </TableCell>
                          <TableCell>$5.81</TableCell>
                          <TableCell>$1.16</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            For tax years beginning on or after January 1, 2026
                          </TableCell>
                          <TableCell>$5.94</TableCell>
                          <TableCell>$1.19</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (c): Deduction Limit Test
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the amount in column (b) is greater than or
                  equal to the potential deduction amount reported in column
                  1(f).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (d): Deduction Amount from Column 1(h)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you checked the box in line 2, column (c), enter the
                    amount from line 1, column (h) into line 2, column (d).
                    Leave columns (e) and (f) blank, then continue with column
                    (g).{' '}
                  </li>
                  <li>
                    If the box in column (c) is not checked, enter 0 in line 2,
                    column (d), and proceed to column (e).
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (e): Deduction Comparison Test
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the amount in column (b) is less than the
                  amount in column 1(f).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (f): Calculated Deduction Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If column (e) is checked, multiply the amount in column (b) by
                  the building square footage reported in column 1(g).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (g): Cost of Energy Efficient Property (EEP)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the cost of qualifying energy-efficient property placed
                  in service during the tax year. Do not include total building
                  costs, general renovations, elevators, escalators, exterior
                  lighting, or manufacturing equipment. If ownership is shared,
                  enter only your allocated share.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (h): Applicable Deduction Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the greater of the amounts calculated in column (d) or
                  column (f). If ownership is less than 100%, enter only your
                  allocated portion.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (i): Allowable Deduction Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the lesser of the qualifying property cost in column (g)
                  or the deduction amount calculated in column (h).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (j): Designer Allocation Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If claiming the deduction as a designer, enter the Section
                  179D deduction amount allocated to you by the tax-exempt
                  building owner.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2, Column (k): Final Section 179D Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Building owners should enter the amount from column (i).
                  Designers should enter the lesser of column (i) or the
                  allocated amount in column (j).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 3: Total Section 179D Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Add all amounts from line 2, column (k), and enter the total
                  deduction on line 3. Report this amount on the applicable line
                  of your federal income tax return and attach Form 7205. If
                  reporting more than four properties, file an additional Form
                  7205 and include the combined deduction total on line 3.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="part3"
                >
                  Part III: Certification Information for Each Property Listed
                  in Part I
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the information for the qualified individual who
                    completed the certification for each property listed in Part
                    I.{' '}
                  </li>
                  <li>
                    If Box 1(e) in Part I is checked, enter the information for
                    the qualified professional who completed the final
                    qualifying certification.{' '}
                  </li>
                  <li>Separately stated income and deduction items </li>
                  <li>
                    Refer to the definitions of{' '}
                    <strong style={{ fontWeight: 600 }}>
                      "Qualified Individual"
                    </strong>{' '}
                    and{' '}
                    <strong style={{ fontWeight: 600 }}>
                      "Qualified Professional"
                    </strong>{' '}
                    in the earlier instructions of the form.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/part-iii-certification-information-7205-img.webp" />
                  <source srcSet="/assets/images/part-iii-certification-information-7205-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part-iii-certification-information-7205-img.png"
                    loading="lazy"
                    alt="Form 7205 Part III showing certification information for each energy efficient property listed in Part I"
                    width={760}
                    height={125}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4(a): Name of Qualified Individual Completing
                  Certification
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full name of the person who completed the
                  certification for the property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4(b): Date of certification
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date on which the certification was completed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4(c): Employer of Qualified Individual
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the employer (company or organization) of
                  the qualified individual.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4(d): Address of Qualified Individual
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the complete mailing or business address of the
                  qualified individual.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="part4"
                >
                  Part IV: Designer Allocation Information for Each Property
                  Listed in Part I (to be completed by Designer only)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/part-iv-designeral-location-7205-img.webp" />
                  <source srcSet="/assets/images/part-iv-designeral-location-7205-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/part-iv-designeral-location-7205-img.png"
                    loading="lazy"
                    alt="Form 7205 Part IV showing designer allocation information for each property listed in Part I"
                    width={760}
                    height={115}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Complete this section only if you are the designer responsible
                  for allocation for the property listed in Part I.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5(a): Identified Owner of Building
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full name of the building owner for the property
                  being reported.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5(b): Date of Allocation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when the allocation was completed by the
                  designer.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5(c): Building Owner’s Authorized Representative (Name)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full name of the authorized representative of the
                  building owner who completed the allocation process.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5(d): Building Owner’s Authorized Representative
                  (Address)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the complete address of the building owner’s authorized
                  representative.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For Rows A, B, C, and D
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Repeat the above details separately for each property listed
                  in Part I, ensuring each entry corresponds correctly to the
                  respective property.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="common7205"
                >
                  What Are the Common Filing Mistakes to Avoid on Form 7205?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Claiming property that does not meet Section 179D
                    requirements.{' '}
                  </li>
                  <li>Missing required certifications. </li>
                  <li>Using incorrect building square footage. </li>
                  <li>
                    Failing to maintain prevailing wage and apprenticeship
                    records.{' '}
                  </li>
                  <li>Incorrectly calculating prior deduction limitations. </li>
                  <li>Forgetting to attach Form 7205 to the tax return. </li>
                  <li>
                    Claiming designer deductions without a valid allocation
                    statement.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="whatshould7205"
                >
                  What Should You Know Before Filing Form 7205?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Verify that the property meets all{' '}
                    <strong style={{ fontWeight: 600 }}>Section 179D</strong>{' '}
                    eligibility requirements.{' '}
                  </li>
                  <li>
                    Retain all{' '}
                    <strong style={{ fontWeight: 600 }}>
                      certifications and supporting
                    </strong>{' '}
                    documentation.{' '}
                  </li>
                  <li>
                    Keep records supporting{' '}
                    <strong style={{ fontWeight: 600 }}>
                      prevailing wage and apprenticeship
                    </strong>{' '}
                    compliance.{' '}
                  </li>
                  <li>
                    Review prior-year{' '}
                    <strong style={{ fontWeight: 600 }}>Section 179D</strong>{' '}
                    deductions before calculating the current deduction.{' '}
                  </li>
                  <li>
                    Use{' '}
                    <strong style={{ fontWeight: 600 }}>
                      additional Forms 7205
                    </strong>{' '}
                    if reporting more than four properties.{' '}
                  </li>
                  <li>
                    Ensure all certifications are completed by{' '}
                    <strong style={{ fontWeight: 600 }}>
                      qualified professionals
                    </strong>{' '}
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  Frequently Asked Questions
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. What is the purpose of Form 7205?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7205 is used to calculate and claim the
                  <strong style={{ fontWeight: 600 }}>
                    Section 179D Energy Efficient Commercial Buildings Deduction
                  </strong>
                  for qualifying energy-efficient commercial building property.
                  It helps taxpayers report eligible property details and
                  determine the allowable deduction amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Can designers claim the deduction?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes.
                  <strong style={{ fontWeight: 600 }}>
                    Eligible designers can claim the Section 179D deduction
                  </strong>
                  &nbsp;when the deduction is allocated to them by a qualifying
                  tax-exempt building owner for designing energy-efficient
                  building property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. What types of property qualify for the Section 179D
                  deduction?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    The Section 179D deduction may apply to
                    <strong style={{ fontWeight: 600 }}>
                      energy-efficient commercial building property (EECBP)
                    </strong>
                    and
                    <strong style={{ fontWeight: 600 }}>
                      energy-efficient building retrofit property (EEBRP)
                    </strong>
                    &nbsp;that meet the applicable IRS requirements.{' '}
                  </li>
                  <li>
                    {' '}
                    EECBP generally includes qualifying improvements to a
                    building’s
                    <strong style={{ fontWeight: 600 }}>
                      interior lighting, heating, cooling, ventilation, hot
                      water, and building envelope systems
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    EEBRP generally refers to qualifying energy-efficient
                    property installed as part of a
                    <strong style={{ fontWeight: 600 }}>
                      qualified retrofit plan
                    </strong>
                    for an existing building. The property must satisfy the
                    applicable energy-efficiency, certification, and other
                    Section 179D requirements.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Is Form 7205 required every year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7205 is required only for tax years when a taxpayer
                  claims the
                  <strong style={{ fontWeight: 600 }}>
                    Section 179D Energy Efficient Commercial Buildings
                    Deduction.
                  </strong>
                  If no deduction is claimed, Form 7205 is generally not
                  required.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. What happens if I do not attach Form 7205?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Form 7205 is required but not attached to the tax return,
                  the taxpayer may
                  <strong style={{ fontWeight: 600 }}>
                    not be able to properly claim the Section 179D deduction.
                  </strong>
                  The IRS may require additional information or deny the
                  deduction until the required form is provided.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file and pay your federal taxes in an easy way?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              File your return and pay securely with Electronic Funds Withdrawal
              (EFW) through TaxZerone.
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
              >
                Start Filing Now
              </Button> */}
      {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Easy. Fast. Precise
              </Typography> */}
      {/* </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default form7205;
